import React, { useRef, useMemo } from 'react';
import { Trash2, X } from 'lucide-react';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import type { FormField, FlowConnectionData, Condition } from '../../types/forms';

interface FlowConditionEditorProps {
  connection: FlowConnectionData;
  sourceField: FormField;
  position: { x: number; y: number };
  zoom: number;
  pan: { x: number; y: number };
  onUpdateCondition: (connectionId: string, condition: Condition | undefined) => void;
  onRemoveConnection: (connectionId: string) => void;
  onClose: () => void;
}

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Igual a' },
  { value: 'not_equals', label: 'Diferente de' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'is_empty', label: 'Está vazio' },
  { value: 'is_not_empty', label: 'Está preenchido' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
];

const NO_VALUE_OPERATORS = ['is_empty', 'is_not_empty'];

const EDITOR_WIDTH = 320;
const EDITOR_HEIGHT_EST = 300;
const VIEWPORT_PADDING = 16;

export const FlowConditionEditor: React.FC<FlowConditionEditorProps> = ({
  connection,
  sourceField,
  position,
  zoom,
  pan,
  onUpdateCondition,
  onRemoveConnection,
  onClose,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const condition = connection.condition;
  const operator = condition?.operator || 'equals';
  const value = condition?.value || '';

  const hasOptions = sourceField.options && sourceField.options.length > 0;
  const showValueInput = !NO_VALUE_OPERATORS.includes(operator);

  // Calculate adjusted position to stay within viewport bounds
  const adjustedPosition = useMemo(() => {
    // Convert canvas coordinates to viewport coordinates
    const vpLeft = position.x * zoom + pan.x;
    const vpTop = position.y * zoom + pan.y;
    const vpWidth = EDITOR_WIDTH * zoom;
    const vpHeight = EDITOR_HEIGHT_EST * zoom;

    let newX = position.x;
    let newY = position.y;

    // Right overflow: shift left
    if (vpLeft + vpWidth > window.innerWidth - VIEWPORT_PADDING) {
      const overflow = vpLeft + vpWidth - (window.innerWidth - VIEWPORT_PADDING);
      newX -= overflow / zoom;
    }

    // Bottom overflow: shift up
    const currentVpTop = newY * zoom + pan.y;
    if (currentVpTop + vpHeight > window.innerHeight - VIEWPORT_PADDING) {
      const overflow = currentVpTop + vpHeight - (window.innerHeight - VIEWPORT_PADDING);
      newY -= overflow / zoom;
    }

    // Left clamp
    const currentVpLeft = newX * zoom + pan.x;
    if (currentVpLeft < VIEWPORT_PADDING) {
      newX = (VIEWPORT_PADDING - pan.x) / zoom;
    }

    // Top clamp
    const finalVpTop = newY * zoom + pan.y;
    if (finalVpTop < VIEWPORT_PADDING) {
      newY = (VIEWPORT_PADDING - pan.y) / zoom;
    }

    return { x: newX, y: newY };
  }, [position, zoom, pan]);

  const handleOperatorChange = (newOperator: string) => {
    const newCondition: Condition = {
      fieldId: sourceField.id,
      operator: newOperator as Condition['operator'],
      value: NO_VALUE_OPERATORS.includes(newOperator) ? undefined : value,
    };
    onUpdateCondition(connection.id, newCondition);
  };

  const handleValueChange = (newValue: string) => {
    const newCondition: Condition = {
      fieldId: sourceField.id,
      operator,
      value: newValue,
    };
    onUpdateCondition(connection.id, newCondition);
  };

  const handleMakeDefault = () => {
    onUpdateCondition(connection.id, undefined);
  };

  return (
    <div
      ref={editorRef}
      className="absolute w-80 liquid-glass rounded-xl border border-neutral-700/50 shadow-2xl transition-all duration-200 ease-out"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        zIndex: 40,
        pointerEvents: 'auto',
        cursor: 'default',
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-700/50">
        <p className="text-xs font-medium text-neutral-200">Condição</p>
        <button
          onClick={onClose}
          className="p-1 rounded text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3 max-h-[400px] overflow-y-auto">
        {/* Source field (readonly) */}
        <div>
          <p className="text-xs text-neutral-500 mb-1">Campo de origem</p>
          <div className="px-3 py-1.5 rounded-lg bg-neutral-800/50 text-sm text-neutral-300">
            {sourceField.label}
          </div>
        </div>

        {/* Operator */}
        <Select
          label="Operador"
          value={operator}
          onChange={(e) => handleOperatorChange(e.target.value)}
          options={OPERATOR_OPTIONS}
        />

        {/* Value */}
        {showValueInput && (
          hasOptions ? (
            <div>
              <p className="text-xs text-neutral-500 mb-1.5">Valor</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {sourceField.options!.map((opt, i) => {
                  const optLabel = typeof opt === 'string' ? opt : opt.label;
                  const optValue = typeof opt === 'string' ? opt : opt.value;
                  const isChecked = Array.isArray(value)
                    ? value.includes(optValue)
                    : value === optValue;

                  return (
                    <label key={i} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-neutral-800/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const current = Array.isArray(value) ? value : value ? [value] : [];
                          const newVal = isChecked
                            ? current.filter(v => v !== optValue)
                            : [...current, optValue];
                          handleValueChange(newVal.length === 1 ? newVal[0] : newVal as any);
                        }}
                        className="rounded border-neutral-600"
                      />
                      <span className="text-sm text-neutral-300">{optLabel}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <Input
              label="Valor"
              value={typeof value === 'string' ? value : ''}
              onChange={(e) => handleValueChange(e.target.value)}
              placeholder="Digite o valor..."
            />
          )
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-neutral-700/50">
          {!connection.isDefault && (
            <button
              onClick={handleMakeDefault}
              className="flex-1 px-2 py-1.5 rounded-lg text-xs text-neutral-400 hover:text-white hover:bg-neutral-800/50 transition-colors"
            >
              Tornar default
            </button>
          )}
          <button
            onClick={() => onRemoveConnection(connection.id)}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Remover
          </button>
        </div>
      </div>
    </div>
  );
};
